<script setup lang="ts">
import { storeToRefs } from 'pinia';

import FAQComponent from '@/components/base/FAQ.vue';
import AccordionTab from '@/components/base/layout/AccordionTab.vue';
import { useAutoDelivery } from '@/stores/autoDelivery';

const { autoDeliveryChannel } = storeToRefs(useAutoDelivery());
</script>

<template>
  <section id="faq" class="w-full px-4 py-16 md:flex md:justify-center md:py-24 bg-nuts-orange-50">
    <FAQComponent class="w-full lg:max-w-5xl md:max-w-3xl" borderColors="gray">
      <AccordionTab :collapsed="false">
        <template v-slot:header>Getting Started</template>
        <template v-slot:content>
          <div>
            <h4 class="pb-6 text-base font-sofia-pro">1. What is Auto-Delivery?</h4>
            <p class="pb-8 text-base font-proxima-nova">
              Auto-Delivery is a recurring shipment subscription service that unlocks
              {{ autoDeliveryChannel.discount }}% savings on thousands of products, excluding
              by-the-case items and custom trays.
            </p>
            <h4 class="pb-6 text-base font-sofia-pro">2. How does Auto-Delivery work?</h4>
            <p class="pb-8 text-base font-proxima-nova">
              When you sign up for Auto-Delivery, you place an order just once – and we'll
              automatically ship your selected goodies at the frequency you choose. Additionally,
              free shipping will be applied to subscription deliveries over $59 sent within the
              continental U.S. Auto-Delivery is currently unavailable for non- U.S. addresses.
              <br />
              <br />
              You can easily manage and customize your orders, from item selection to delivery
              schedule. We’ll always send an email reminder three days before each order ships,
              providing an overview of your selected items.<br />
              <br />
              You can cancel your order or change your delivery frequency, next order date, or
              quantity at any time by visiting the
              <RouterLink to="/account/auto-delivery" class="no-underline">
                Auto-Delivery
              </RouterLink>
              page in the
              <RouterLink to="/account" class="no-underline">Your Account</RouterLink>
              section of our site. Changes to orders can be made up to 24 hours prior to the next
              scheduled shipment date.
            </p>
            <h4 class="pb-6 text-base font-sofia-pro">
              3. How do I create an Auto-Delivery subscription?
            </h4>
            <p class="pb-8 text-base font-proxima-nova">
              You can create a subscription while browsing a product page or reviewing items in the
              shopping cart. To subscribe, simply select the Auto-Delivery option, select your
              preferred shipment frequency, and then check out as you normally would.
            </p>
            <h4 class="pb-6 text-base font-sofia-pro">
              4. What will happen after I create a subscription?
            </h4>
            <p class="pb-8 text-base font-proxima-nova">
              After you join Auto-Delivery, you will receive an email confirming your subscription.
              This email will include a link to access the
              <RouterLink to="/account/auto-delivery" class="no-underline">
                Auto-Delivery
              </RouterLink>
              page located in the
              <RouterLink to="/account" class="no-underline">Your Account</RouterLink> section of
              our site, where you can manage your future orders, subscription items and account
              information.<br />
              <br />
              Your Auto-Delivery orders will be placed automatically based on your selected delivery
              frequency. You will only be charged on the day your order is shipped. We’ll send you
              an email reminder three days before the shipment day.<br />
              <br />
              You will also receive email notifications regarding important information related to
              your Auto-Delivery account, such as if a product is discontinued, if you cancel your
              subscription, or once your order is successfully placed.
            </p>
            <h4 class="pb-6 text-base font-sofia-pro">
              5. How do I ensure I receive the discount on my subscription?
            </h4>
            <p class="text-base font-proxima-nova">
              After signing up, you will automatically receive the
              {{ autoDeliveryChannel.discount }}% discount on your first and all future orders. The
              discount will not be applied to by-the-case items.<br />
              <br />
              Furthermore, we’ll apply free shipping when you spend $59 or more on subscription
              products, including by-the-case items. All items must be set to ship on the same day
              to all the addresses you provide.
            </p>
          </div>
        </template>
      </AccordionTab>
      <AccordionTab>
        <template v-slot:header>Subscription Management</template>
        <template v-slot:content>
          <h4 class="pb-6 text-base font-sofia-pro">
            1. How do I make changes to my Auto-Delivery orders?
          </h4>
          <p class="pb-8 text-base font-proxima-nova">
            Once you’ve subscribed, you can manage your Auto-Delivery
            <RouterLink to="/account/auto-delivery" class="no-underline">here</RouterLink>. From
            there, you can change your shipment date, delivery schedule and more.
          </p>
          <h4 class="pb-6 text-base font-sofia-pro">2. How do I change my delivery schedule?</h4>
          <p class="pb-8 text-base font-proxima-nova">
            Visit the
            <RouterLink to="/account/auto-delivery" class="no-underline">
              Auto-Delivery page
            </RouterLink>
            on
            <RouterLink to="/account" class="no-underline"> Your Account </RouterLink>. On this tab,
            you’ll find the current delivery frequency next to each subscription item in a drop-down
            menu. Click on the arrow next to the item's frequency drop-down and select your desired
            delivery frequency.
          </p>
          <h4 class="pb-6 text-base font-sofia-pro">
            3. How do I change the shipment date for my Auto-Delivery order?
          </h4>
          <p class="pb-8 text-base font-proxima-nova">
            If you’d like to postpone or move up the shipment date, visit the
            <RouterLink to="/account/auto-delivery" class="no-underline">
              Auto-Delivery page
            </RouterLink>
            on <RouterLink to="/account" class="no-underline"> Your Account </RouterLink>. Click the
            Change Date button near the next shipment date, then select a new date for your order.
            This will change the shipment date for the entire order.<br />
            <br />
            If your order contains multiple items and you only wish to change the shipment date for
            a single item, please visit the
            <RouterLink to="/account/auto-delivery" class="no-underline">
              Auto-Delivery page
            </RouterLink>
            on
            <RouterLink to="/account" class="no-underline"> Your Account </RouterLink> and find the
            individual item you would like to modify. Click Postpone subscription for the individual
            item and select your desired shipment date from the pop-up calendar.
          </p>
          <h4 class="pb-6 text-base font-sofia-pro">4. How do I skip a delivery?</h4>
          <p class="pb-8 text-base font-proxima-nova">
            Visit
            <RouterLink to="/account/auto-delivery" class="no-underline">
              Auto-Delivery page
            </RouterLink>
            on
            <RouterLink to="/account" class="no-underline"> Your Account </RouterLink> and click the
            Skip Delivery button near the Next Shipment Date button to skip the entire order.
          </p>
          <h4 class="pb-6 text-base font-sofia-pro">
            5. How do I add and remove items to/from my Auto-Delivery order?
          </h4>
          <p class="pb-8 text-base font-proxima-nova">
            Each product page features the option to select Auto-Delivery and the delivery
            frequency. To add an item, visit its product page and click the Add to Auto-Delivery
            button.<br />
            <br />
            To remove items, visit the
            <RouterLink to="/account/auto-delivery" class="no-underline">
              Auto-Delivery page
            </RouterLink>
            on
            <RouterLink to="/account" class="no-underline"> Your Account </RouterLink>, find the
            items you wish to eliminate and then click on the Remove From This Shipment button.
          </p>
          <h4 class="pb-6 text-base font-sofia-pro">
            6. How do I add an item as a one-time Auto-Delivery?
          </h4>
          <p class="pb-8 text-base font-proxima-nova">
            Each product page features an Add to Auto-Delivery button. Clicking on it will open a
            pop-up window where you can choose Future Auto-Delivery or One-Time Delivery.
          </p>
          <h4 class="pb-6 text-base font-sofia-pro">
            7. How do I cancel my Auto-Delivery subscription?
          </h4>
          <p class="text-base font-proxima-nova">
            Visit the
            <RouterLink to="/account/auto-delivery" class="no-underline">
              Auto-Delivery page
            </RouterLink>
            and click the Cancel subscription button. You’ll be prompted to confirm this action and
            indicate your reason for canceling. Once you’ve submitted, your subscription will be
            canceled and no future orders will be placed. You will also receive an email
            notification confirming this. Please allow up to 24 hours for any modifications or
            cancellations to be processed.
          </p>
        </template>
      </AccordionTab>
      <AccordionTab>
        <template v-slot:header>Account Information</template>
        <template v-slot:content>
          <h4 class="pb-6 text-base font-sofia-pro">
            1. How do I make changes to my Auto-Delivery account information?
          </h4>
          <p class="pb-8 text-base font-proxima-nova">
            Visit the
            <RouterLink to="/account/auto-delivery" class="no-underline">
              Auto-Delivery page
            </RouterLink>
            to review or modify your Auto-Delivery account information. You can view or change your
            shipping address or payment information for an individual order.
          </p>
          <h4 class="pb-6 text-base font-sofia-pro">2. How do I change shipping information?</h4>
          <p class="pb-8 text-base font-proxima-nova">
            Visit the
            <RouterLink to="/account/auto-delivery" class="no-underline">Auto-Delivery</RouterLink>
            on your account and select Edit in the Shipping Info section.
          </p>
          <h4 class="pb-6 text-base font-sofia-pro">
            3. How do I change my payment method and billing information?
          </h4>
          <p class="pb-8 text-base font-proxima-nova">
            Go to the <RouterLink to="/account/wallet" class="no-underline">Wallet</RouterLink> page
            located in the
            <RouterLink to="/account" class="no-underline">Your Account</RouterLink> section of our
            site. Please remember to press OK to verify that your updated information is saved
            successfully. We kindly ask that you allow up to 24 hours for modifications or
            cancellations to be processed.
          </p>
        </template>
      </AccordionTab>
    </FAQComponent>
  </section>
</template>
